import React from 'react';
import { Button, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import s from './s.module.less';
import Benefits from './Benefits';
import FAQComponent from './FAQ';
import HowItWork from './HowItWork';
import Frame from './imgs/Frame.png';
import Avatar from './imgs/avatar.png';
import SureIcon from './imgs/logo.png';
import Fee from './Fee';
import useKanmonConnect from 'hooks/useKanmonConnect';
import { getKanmonUiView } from 'utils/kanmonUi';

enum ESection {
    BENEFITS = 'benefits',
    FAQ = 'faq',
    FEE = 'fee',
    HOW_IT_WORKS = 'howItWorks',
}

type Props = {
    isModal?: boolean;
    onClickedApplyNow: () => void;
    doShowKanmonModal?: () => void;
};

const FinancialsLandingPage = (props: Props) => {
    const { isModal, onClickedApplyNow, doShowKanmonModal } = props;
    const [hasClickApplyNow, setHasClickApplyNow] = React.useState(false);
    const [kanmonConnect, kanmonInfo, loadingKanmonConnect, refetchKanmonInfo, ui] = useKanmonConnect(hasClickApplyNow);
    const [activeId, setActiveId] = React.useState(ESection.BENEFITS);
    const view = getKanmonUiView(ui, !!kanmonInfo?.kanmonBusinessId);

    React.useEffect(() => {
        if (!hasClickApplyNow) return;
        if (ui.status === 'error') {
            setHasClickApplyNow(false);
        } else if (kanmonConnect) {
            setHasClickApplyNow(false);
            onClickedApplyNow?.();
        }
    }, [hasClickApplyNow, kanmonConnect, ui.status, onClickedApplyNow]);

    const guideItems: TabsProps['items'] = [
        {
            key: ESection.BENEFITS,
            label: <div>Benefits</div>,
            children: <div />,
        },
        {
            key: ESection.HOW_IT_WORKS,
            label: <div>How it works</div>,
            children: <div />,
        },
        {
            key: ESection.FAQ,
            label: <div>FAQs</div>,
            children: <div />,
        },
    ];

    const handleChange = (activeKey: string) => {
        setActiveId(activeKey as ESection);
        const element = document.getElementById(activeKey as string);
        let wrapElement: Element | null = document.getElementById('contentWrap');
        if (isModal) {
            wrapElement = document.getElementsByClassName('ant-modal-body')[0];
        }
        wrapElement?.scrollTo({
            top: (element as HTMLBaseElement).offsetTop - 150,
            behavior: 'smooth',
        });
    };

    React.useEffect(() => {
        const wrap = document.getElementById('contentWrap');
        const second = document.getElementById(ESection.HOW_IT_WORKS);
        const third = document.getElementById(ESection.FEE);
        const fourth = document.getElementById(ESection.FAQ);
        const handle = () => {
            const top = wrap?.scrollTop;
            const slug = 500;
            if (fourth && top && (top + slug) > fourth?.offsetTop) {
                setActiveId(ESection.FAQ);
            } else if (third && top && (top + slug) > third?.offsetTop) {
                setActiveId(ESection.FEE);
            } else if (second && top && (top + slug) > second?.offsetTop) {
                setActiveId(ESection.HOW_IT_WORKS);
            } else {
                setActiveId(ESection.BENEFITS);
            }
        };
        wrap?.addEventListener('scroll', handle);
        return () => {
            wrap?.removeEventListener('scroll', handle);
        };
    }, []);

    return (
        <div
            className={`${s.wrap} ${isModal ? s.wrapModal : ''}`}
            id="contentWrap"
        >
            <div className={s.header}>
                <div className={s.left}>
                    <div className={s.title}>
                        See how Klarity Pay Later can help you accelerate your patient growth — without financial stress (In partnership with
                        <img className={s.icon} src={SureIcon} />)
                    </div>
                    <div className={s.des}>
                        Klarity marketing services help your practice gain new patients quickly. Klarity Pay Later helps you grow even faster by removing upfront marketing costs. Invest in visibility and patient growth now — this program eliminates financial barriers so you can focus on your patients.
                    </div>
                    <div className={s.startBtn}>
                        <Button
                            className={s.btn}
                            loading={hasClickApplyNow || loadingKanmonConnect || ui.status === 'loading'}
                            onClick={() => {
                                if (view.primaryAction === 'apply') {
                                    setHasClickApplyNow(true);
                                } else if (view.primaryAction === 'retry' || !kanmonConnect) {
                                    refetchKanmonInfo();
                                } else if (doShowKanmonModal) {
                                    doShowKanmonModal?.();
                                } else {
                                    kanmonConnect.show();
                                }
                            }}
                        >
                            {view.ctaText}
                        </Button>
                    </div>

                </div>
                <div className={s.right}>
                    <div className={s.avWrap}>
                        <img src={Avatar} />
                        <p>{kanmonInfo?.joinedProviderCount} providers joined</p>
                    </div>
                    <img className={s.frame} src={Frame} />
                </div>
            </div>
            <div className={s.outContent}>
                <div className={s.tabs}>
                    <Tabs
                        activeKey={activeId}
                        items={guideItems}
                        onChange={(activeKey) => handleChange(activeKey)}
                    />
                </div>
                <div className={s.content} id={ESection.BENEFITS}>
                    <Benefits />
                </div>
                <div className={s.content} id={ESection.HOW_IT_WORKS}>
                    <HowItWork />
                </div>
                <div className={s.content} id={ESection.FEE}>
                    <Fee />
                </div>
                <div className={s.content} id={ESection.FAQ}>
                    <FAQComponent />
                </div>
            </div>
        </div>
    );
};

export default FinancialsLandingPage;
